import Auth from "@aws-amplify/auth";
import store from "@/store";
import McusDataService from "@/services/McusDataService";
import { isGlobalLoading } from "@/store/autoLogoutLoading";

// ============================================================
// 定数
// ============================================================
const DEFAULT_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2時間（ミリ秒）
const STORAGE_KEY_TIMEOUT = "nextAutoLogoutAt";

// ============================================================
// モジュール変数
// ============================================================
let timeoutMs = DEFAULT_TIMEOUT_MS;
let setTimeoutID = null;
let intervalID = null;
let setIdleTimer = null;

// throttle された mousemove ハンドラ（removeEventListener のために参照を保持）
let throttledMouseMoveHandler = null;

// ============================================================
// ユーティリティ
// ============================================================

/**
 * 指定した関数を delay ミリ秒に1回だけ呼び出す throttle 関数
 */
function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
}

// ============================================================
// タイムアウト値の取得
// ============================================================

/**
 * SSM Parameter Store からタイムアウト秒数を取得して timeoutMs に設定する。
 * 取得失敗時はデフォルト値（4時間）を使用する。
 */
async function fetchAndSetTimeout() {
  try {
    const response = await McusDataService.getLogoutTimeout();
    timeoutMs = response.data.logoutTimeoutSeconds * 1000;
  } catch (e) {
    console.warn("Failed to fetch logoutTimeoutSeconds, using default:", e);
    timeoutMs = DEFAULT_TIMEOUT_MS;
  }
}

// ============================================================
// タイマー制御
// ============================================================

/**
 * setTimeout タイマーをクリアする
 */
function clearAutoLogoutTimer() {
  if (setTimeoutID !== null) {
    clearTimeout(setTimeoutID);
    setTimeoutID = null;
  }
}

/**
 * setInterval による定期チェックを開始する（30秒間隔）
 */
function startIntervalCheck() {
  if (intervalID !== null) return; // 二重起動防止
  intervalID = setInterval(() => {
    const nextLogoutTime = parseInt(localStorage.getItem(STORAGE_KEY_TIMEOUT), 10);
    if (nextLogoutTime && Date.now() > nextLogoutTime) {
      autoLogout();
    }
  }, 30 * 1000);
}

/**
 * setInterval による定期チェックを停止する
 */
function stopIntervalCheck() {
  if (intervalID !== null) {
    clearInterval(intervalID);
    intervalID = null;
  }
}

// ============================================================
// アイドルタイマーのセット
// ============================================================

/**
 * アイドルタイマーをリセットするクロージャを生成して返す。
 * 操作検知のたびに呼び出し、localStorage と setTimeout を更新する。
 */
function createSetIdleTimer() {
  return () => {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY_TIMEOUT, now + timeoutMs);
    clearAutoLogoutTimer();
    setTimeoutID = setTimeout(() => {
      // 他タブの操作で localStorage が更新されている場合はログアウトしない
      const nextLogoutTime = parseInt(localStorage.getItem(STORAGE_KEY_TIMEOUT), 10);
      if (nextLogoutTime && Date.now() > nextLogoutTime) {
        autoLogout();
      }
    }, timeoutMs);
  };
}

// ============================================================
// イベントリスナー
// ============================================================

/**
 * ユーザー操作・ページ復帰を監視するイベントリスナーを登録する。
 *
 * 監視イベント:
 *   - click        : マウスクリック・タップ
 *   - touchstart   : タップ・スワイプ
 *   - keydown      : キーボード入力（フォーム操作を確実に検知）
 *   - mousedown    : マウスボタン押下
 *   - scroll       : スクロール操作（passive）
 *   - mousemove    : マウス移動（throttle 1秒）
 *   - visibilitychange : タブ復帰の検知
 *   - focus        : ウィンドウフォーカス復帰の検知
 */
function addEventListeners() {
  // mousemove は発火頻度が高いため throttle（1秒に1回）
  throttledMouseMoveHandler = throttle(() => setIdleTimer(), 1000);

  window.addEventListener("click", setIdleTimer);
  window.addEventListener("touchstart", setIdleTimer);
  window.addEventListener("keydown", setIdleTimer);
  window.addEventListener("mousedown", setIdleTimer);
  window.addEventListener("scroll", setIdleTimer, { passive: true });
  window.addEventListener("mousemove", throttledMouseMoveHandler);

  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("focus", handleWindowFocus);
}

/**
 * 登録したイベントリスナーを全て解除する
 */
function removeEventListeners() {
  window.removeEventListener("click", setIdleTimer);
  window.removeEventListener("touchstart", setIdleTimer);
  window.removeEventListener("keydown", setIdleTimer);
  window.removeEventListener("mousedown", setIdleTimer);
  window.removeEventListener("scroll", setIdleTimer);
  if (throttledMouseMoveHandler) {
    window.removeEventListener("mousemove", throttledMouseMoveHandler);
    throttledMouseMoveHandler = null;
  }

  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("focus", handleWindowFocus);
}

// ============================================================
// ページ復帰時のチェック
// ============================================================

function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    checkAutoLogoutOnStartup();
  }
}

function handleWindowFocus() {
  checkAutoLogoutOnStartup();
}

// ============================================================
// 公開 API
// ============================================================

/**
 * アプリ起動時・タブ復帰時に呼び出す。
 * localStorage の nextAutoLogoutAt が現在時刻を超えていればログアウトする。
 */
export function checkAutoLogoutOnStartup() {
  const nextLogoutTime = parseInt(localStorage.getItem(STORAGE_KEY_TIMEOUT), 10);
  if (nextLogoutTime && Date.now() > nextLogoutTime) {
    autoLogout();
  }
}

/**
 * 自動ログアウト機能をセットアップする。
 * ログイン成功後・アプリ起動時（認証済みの場合）に呼び出す。
 */
export async function setupAutoLogout() {
  // タイムアウト値を SSM から取得
  await fetchAndSetTimeout();

  // アイドルタイマーのクロージャを生成
  setIdleTimer = createSetIdleTimer();

  // イベントリスナーを登録
  addEventListeners();

  // 初回のアイドルタイマーをセット（ログイン直後に nextAutoLogoutAt をリセット）
  setIdleTimer();

  // setInterval による定期チェックを開始
  startIntervalCheck();
}

/**
 * 自動ログアウト機能をクリーンアップする。
 * ログアウト時（自動・手動いずれも）に呼び出す。
 */
export function cleanupAutoLogout() {
  clearAutoLogoutTimer();
  stopIntervalCheck();
  localStorage.removeItem(STORAGE_KEY_TIMEOUT);
  removeEventListeners();
  setTimeoutID = null;
  setIdleTimer = null;
}

/**
 * 自動ログアウト処理を実行する。
 * タイムアウト・起動時チェック・タブ復帰チェック・Cognito セッション切れ時に呼び出す。
 */
export async function autoLogout() {
  try {
    isGlobalLoading.value = true;
    cleanupAutoLogout();

    // 別タブ等ですでにセッションが破棄されている場合は Hosted UI logout に飛ばさず '/' へ遷移する
    try {
      await Auth.currentSession();
    } catch (e) {
      window.location.href = "/";
      return;
    }

    await store.dispatch("logout");
    const logoutUri = `https://${process.env.VUE_APP_AWS_COGNITO_DOMAIN_PREFIX}.auth.eu-central-1.amazoncognito.com/logout?client_id=${process.env.VUE_APP_AWS_COGNITO_USER_WEB_CLIENT_ID}&logout_uri=${process.env.VUE_APP_AWS_COGNITO_REDIRECT_URL}`;
    window.location.href = logoutUri;
  } catch (error) {
    console.error("Error during auto sign out:", error);
    isGlobalLoading.value = false;
  }
}
